import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Card, CardBody, Button, Form, Modal, ModalHeader, ModalBody, ModalFooter, Input } from "reactstrap";
import CommonTableIndexWise from "../../../Components/Common/CommonTableIndexWise";
import CommonForm from "../../../Components/Common/CommonForm";
import sf from "../../../assets/scss/common/searchFilter.module.scss";
import ac from "../css/scanning.module.scss";
import { useForm } from "react-hook-form";
import { useScanningScanSampleCollectionService } from "../api/useScanningSession";
import { useQueryClient } from "@tanstack/react-query";
import { formatDateTime } from "helpers/crf.utils";
import { getScanFields } from "../helper/scanningconstant";
import { getSessionLogColumns } from "modules/Session/Session/helper/sessionlogconstant";
import { GetUserData } from "common/service/CommonService";
import SkipReasonModal from "./SkipReasonModal";

interface SubjectToVacutainerProps {
  sessionData?: any;
  sessionid?: string;
  isParentModalOpen?: boolean;
}


const SubjectToVacutainerScanning: React.FC<SubjectToVacutainerProps> = ({
  sessionData,
  sessionid,
  isParentModalOpen,
}) => {
  // State
  const [subjectStatus, setSubjectStatus] = useState<
    "idle" | "matched" | "mismatch"
  >("idle");
  const [vacutainerStatus, setVacutainerStatus] = useState<
    "idle" | "matched" | "mismatch"
  >("idle");
  const [formMessage, setFormMessage] = useState<{
    text: string;
    type: "success" | "error" | "";
  }>({ text: "", type: "" });

  const totalSubjectsCount = sessionData?.total_subjects ?? sessionData?.totalsubjects ?? 0;
  const skippedSubjectsCount = sessionData?.skipped_subjects ?? 0;
  const completedSubjectsCount = (sessionData?.completed_subjects ?? 0) + skippedSubjectsCount;
  const completedVacutainersCount = (sessionData?.completed_vaccutainers ?? 0) + skippedSubjectsCount;
  const isCollectionScanCompleted =
    sessionData?.display_status === "COMPLETED" ||
    sessionData?.sessionstate === "completed" ||
    (totalSubjectsCount > 0 &&
      skippedSubjectsCount === 0 &&
      completedSubjectsCount >= totalSubjectsCount &&
      completedVacutainersCount >= totalSubjectsCount);

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isShowingAllScans, setIsShowingAllScans] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [skipReason, setSkipReason] = useState("");
  const [searchText, setSearchText] = useState<string>("");
  const [searchFocused, setSearchFocused] = useState(false);

  const [currentPageNumber, setcurrentPageNumber] = useState(1);

  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    setValue,
  } = useForm({
    defaultValues: { subjectBarcode: "", vacutainerBarcode: "" },
  });

  const { InsertMutation: ScanInsertMutation, ValidateSubjectMutation, SelectAllQuery } =
    useScanningScanSampleCollectionService({
      sessionid: sessionid ? parseInt(sessionid) : sessionData?.sessionid,
      searchtext: searchText || "",
      currentpage: currentPageNumber,
      pagesize: 10,
    });

  const subjectBarcode = watch("subjectBarcode");
  const vacutainerBarcode = watch("vacutainerBarcode");

  // Scanner vs Manual Detection
  const lastKeyTimeRef = useRef<number>(0);
  const keystrokeIntervalsRef = useRef<number[]>([]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "Enter" ||
        e.key === "Shift" ||
        e.key === "Control" ||
        e.key === "Tab"
      )
        return;
      const now = Date.now();
      if (lastKeyTimeRef.current !== 0) {
        const gap = now - lastKeyTimeRef.current;
        if (gap < 1000) keystrokeIntervalsRef.current.push(gap);
      }
      lastKeyTimeRef.current = now;
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    let attempts = 0;
    const focusInterval = setInterval(() => {
      try {
        const targetName =
          subjectStatus === "idle" ? "subjectBarcode" : "vacutainerBarcode";
        const input = document.getElementsByName(
          targetName,
        )[0] as HTMLInputElement;
        if (input && !input.disabled) {
          input.focus();
          if (document.activeElement === input || attempts >= 15) {
            clearInterval(focusInterval);
          }
        }
        attempts++;
        if (attempts >= 15) clearInterval(focusInterval);
      } catch (err) {
        attempts++;
      }
    }, 300);

    return () => clearInterval(focusInterval);
  }, [subjectStatus, isShowingAllScans, isParentModalOpen]);

  const activeUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const speechTimeoutRef = useRef<any>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
        speechTimeoutRef.current = null;
      }
      if (activeUtteranceRef.current && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
        activeUtteranceRef.current = null;
      }
    };
  }, []);

  const speak = (msg: string, onEndCallback?: () => void) => {
    if (!("speechSynthesis" in window)) {
      onEndCallback?.();
      return;
    }

    const synth = window.speechSynthesis;

    if (speechTimeoutRef.current) {
      clearTimeout(speechTimeoutRef.current);
      speechTimeoutRef.current = null;
    }

    if (synth.speaking || synth.pending) {
      synth.cancel();
    }

    if (isMountedRef.current) {
      setIsSpeaking(true);
    }

    // 150ms delay to allow browser speech synthesis engine to clear/reset queue
    speechTimeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      try {
        const utterance = new SpeechSynthesisUtterance(msg);
        utterance.rate = 1.0; // 1.0 is standard natural speech speed
        utterance.lang = "en-US";

        const voices = synth.getVoices();
        const voice =
          voices.find((v) => v.lang === "en-US") ||
          voices.find((v) => v.lang.startsWith("en")) ||
          voices[0];

        if (voice) utterance.voice = voice;
        activeUtteranceRef.current = utterance;

        const cleanUp = () => {
          if (isMountedRef.current) {
            setIsSpeaking(false);
          }
          activeUtteranceRef.current = null;
        };

        utterance.onend = () => {
          cleanUp();
          if (isMountedRef.current) {
            onEndCallback?.();
          }
        };

        utterance.onerror = () => {
          cleanUp();
          if (isMountedRef.current) {
            onEndCallback?.();
          }
        };

        synth.speak(utterance);
      } catch (err) {
        console.error("SpeechSynthesis error in SubjectToVacutainer:", err);
        if (isMountedRef.current) {
          setIsSpeaking(false);
          onEndCallback?.();
        }
      }
    }, 150);
  };

  const clearAndRestart = () => {
    setSubjectStatus("idle");
    setVacutainerStatus("idle");
    setFormMessage({ text: "", type: "" });
    clearErrors();
    reset({ subjectBarcode: "", vacutainerBarcode: "" });
    setTimeout(() => {
      const firstInput = document.getElementsByName(
        "subjectBarcode",
      )[0] as HTMLInputElement;
      if (firstInput) firstInput.focus();
    }, 100);
  };

  const validateBarcodes = (sub_barcode: string, vac_barcode: string) => {
    // if (sub_barcode.length !== 10) return "Subject barcode length must be 10 characters";
    if (vac_barcode.length !== 10)
      return "vacutainer barcode length must be 10 characters";

    // Extract values
    const sub_suffix = sub_barcode.substring(6, 10); // last 4 digits
    const sub_prefix = sub_barcode.substring(0, 6);

    const vac_prefix = vac_barcode.substring(0, 6);
    const period = parseInt(vac_barcode.substring(6, 7));
    const timepointBarcode = vac_barcode.substring(7, 9);
    const v_type = parseInt(vac_barcode.substring(9, 10));

    const timepointSession =
      sessionData?.timepoint_sort < 10
        ? `0${sessionData?.timepoint_sort}`
        : `${sessionData?.timepoint_sort}`;

    // VALIDATIONS
    // if (sub_suffix !== "0000") return "Subject barcode suffix must be 0000";
    if (v_type !== 1) return "Not a vacutainer barcode";

    if (period !== sessionData?.period_sort) return "Invalid period";
    if (timepointBarcode !== timepointSession) return "Invalid timepoint";

    if (sub_prefix !== vac_prefix) return "Barcode Mismatch";

    return "success";
  };

  const handleScanSubmit = (data: any, e?: React.BaseSyntheticEvent) => {
    clearErrors();
    const hasSubject = data.subjectBarcode && data.subjectBarcode.trim() !== "";
    const hasVacutainer =
      data.vacutainerBarcode && data.vacutainerBarcode.trim() !== "";
    const submitterName = (e?.nativeEvent as any)?.submitter?.name;
    const isManualClick = submitterName === "verifyButton";

    const intervals = keystrokeIntervalsRef.current;
    const avgInterval =
      intervals.length > 0
        ? intervals.reduce((a, b) => a + b, 0) / intervals.length
        : 0;
    const isScanner =
      !isManualClick && (intervals.length === 0 || avgInterval < 50);

    keystrokeIntervalsRef.current = [];
    lastKeyTimeRef.current = 0;

    if (hasSubject && !hasVacutainer && !isManualClick) {
      const sub_barcode = data.subjectBarcode.trim();
      // Subject-only validation before moving to next field
      if (sub_barcode.length !== 10) {
        setSubjectStatus("mismatch");
        setFormMessage({
          text: "Subject barcode length must be 10 characters",
          type: "error",
        });
        speak("Error. Subject barcode length is invalid.", () => {
          setValue("subjectBarcode", "");
          clearErrors("subjectBarcode");
          setFormMessage({ text: "", type: "" });
          setTimeout(() => {
            const input = document.getElementsByName(
              "subjectBarcode",
            )[0] as HTMLInputElement;
            if (input) input.focus();
          }, 100);
        });
        return;
      }

      if (sub_barcode.substring(6, 10) !== "0000") {
        setSubjectStatus("mismatch");
        setFormMessage({ text: "Invalid subject barcode", type: "error" });
        speak("Error. Invalid subject barcode", () => {
          setValue("subjectBarcode", "");
          clearErrors("subjectBarcode");
          setFormMessage({ text: "", type: "" });
          setTimeout(() => {
            const input = document.getElementsByName(
              "subjectBarcode",
            )[0] as HTMLInputElement;
            if (input) input.focus();
          }, 100);
        });
        return;
      }

      ValidateSubjectMutation.mutate(
        {
          sessionid: sessionid
            ? parseInt(sessionid)
            : sessionData?.sessionid,
          subjectbarcode: sub_barcode,
        },
        {
          onSuccess: (res: any) => {
            setSubjectStatus("matched");
            const focusNext = () => {
              setTimeout(() => {
                const nextInput = document.getElementsByName(
                  "vacutainerBarcode",
                )[0] as HTMLInputElement;
                if (nextInput) nextInput.focus();
              }, 100);
            };

            const msg = res?.message || "Subject Scanned. Waiting for vacutainer.";
            if (isScanner) {
              setFormMessage({
                text: msg,
                type: "success",
              });
              speak(msg, focusNext);
            } else {
              setFormMessage({ text: msg, type: "success" });
              focusNext();
            }
            setTimeout(() => {
              setFormMessage({ text: "", type: "" });
            }, 1500);
          },
          onError: (err: any) => {
            const errorData = err?.response?.data || err;
            let backendMsg =
              typeof errorData === "string"
                ? errorData
                : errorData?.message ||
                  errorData?.resultmessage ||
                  errorData?.result ||
                  err?.message;

            const parsedMsg = Array.isArray(backendMsg)
              ? backendMsg.join(", ")
              : backendMsg;
            const displayMsg =
              typeof parsedMsg === "string" && parsedMsg
                ? parsedMsg
                : "Invalid subject barcode";

            setSubjectStatus("mismatch");
            setFormMessage({ text: displayMsg, type: "error" });
            speak(`Error. ${displayMsg}`, () => {
              setValue("subjectBarcode", "");
              clearErrors("subjectBarcode");
              setFormMessage({ text: "", type: "" });
              setTimeout(() => {
                const input = document.getElementsByName(
                  "subjectBarcode",
                )[0] as HTMLInputElement;
                if (input) input.focus();
              }, 100);
            });
          },
        }
      );
      return;
    }

    if (!hasSubject) {
      setError("subjectBarcode", {
        type: "manual",
        message: "Subject barcode cannot be empty.",
      });
      setFormMessage({
        text: "Subject barcode cannot be empty.",
        type: "error",
      });
      speak("Error. Subject barcode cannot be empty.", () => {
        setValue("subjectBarcode", "");
        clearErrors("subjectBarcode");
        setFormMessage({ text: "", type: "" });
        setTimeout(() => {
          const input = document.getElementsByName(
            "subjectBarcode",
          )[0] as HTMLInputElement;
          if (input) input.focus();
        }, 100);
      });
      return;
    }

    if (!hasVacutainer) {
      setError("vacutainerBarcode", {
        type: "manual",
        message: "Vacutainer barcode cannot be empty.",
      });
      setFormMessage({
        text: "Vacutainer barcode cannot be empty.",
        type: "error",
      });
      speak("Error. Vacutainer barcode cannot be empty.", () => {
        setValue("vacutainerBarcode", "");
        clearErrors("vacutainerBarcode");
        setFormMessage({ text: "", type: "" });
        setTimeout(() => {
          const input = document.getElementsByName(
            "vacutainerBarcode",
          )[0] as HTMLInputElement;
          if (input) input.focus();
        }, 100);
      });
      return;
    }

    const validationResult = validateBarcodes(
      data.subjectBarcode.trim(),
      data.vacutainerBarcode.trim(),
    );

    if (validationResult === "success") {
      setVacutainerStatus("matched");
      setSubjectStatus("matched");
      ScanInsertMutation.mutate(
        {
          sessionid: sessionid
            ? parseInt(sessionid)
            : sessionData?.sessionid || 1,
          subjectbarcode: data.subjectBarcode.trim(),
          vaccutainerbarcode: data.vacutainerBarcode.trim(),
          result: "valid",
          resultmessage: "Match Successfully",
        },
        {
          onSuccess: (res: any) => {
            queryClient.invalidateQueries({
              queryKey: ["scanning-scan-sample-collection-select-all"],
            });
            queryClient.invalidateQueries({ queryKey: ["scanning-session-select"] });
            queryClient.invalidateQueries({ queryKey: ["scanning-session-progress"] });
            const msg = res?.message || "Match Verified.";
            setFormMessage({ text: msg, type: "success" });
            speak("Barcode Matched.");
            setTimeout(() => {
              clearAndRestart();
            }, 1200);
          },
          onError: (err: any) => {
            // 1. Get the actual error payload whether it's wrapped in Axios response or not
            const errorData = err?.response?.data || err;

            // 2. Extract the message directly from the backend's JSON structure or string
            let backendMsg =
              typeof errorData === "string"
                ? errorData
                : errorData?.message ||
                errorData?.resultmessage ||
                errorData?.result ||
                err?.message;

            const parsedMsg = Array.isArray(backendMsg)
              ? backendMsg.join(", ")
              : backendMsg;
            const displayMsg =
              typeof parsedMsg === "string" && parsedMsg
                ? parsedMsg
                : "Failed to record scan";

            // 3. Check if it's an "already exists" error (by status code or text)
            const is409 =
              err?.response?.status === 409 ||
              errorData?.statusCode === 409 ||
              errorData?.status === 409 ||
              displayMsg.toLowerCase().includes("already exist");

            // 4. Set final messages
            const speakMsg = is409 ? "Already exists" : displayMsg;

            setFormMessage({ text: displayMsg, type: "error" });
            speak(`Error. ${speakMsg}`);
            setTimeout(() => {
              clearAndRestart();
            }, 2500);
          },
        },
      );
    } else {
      setVacutainerStatus("mismatch");
      setFormMessage({ text: validationResult, type: "error" });

      // API CALL for failure (specifically ONLY when sub_prefix !== vac_prefix)
      if (validationResult === "Barcode Mismatch") {
        ScanInsertMutation.mutate(
          {
            sessionid: sessionid
              ? parseInt(sessionid)
              : sessionData?.sessionid || 1,
            subjectbarcode: data.subjectBarcode.trim(),
            vaccutainerbarcode: data.vacutainerBarcode.trim(),
            result: "invalid",
            resultmessage: "mismatched barcode",
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: ["scanning-scan-sample-collection-select-all"],
              });
              queryClient.invalidateQueries({ queryKey: ["scanning-session-select"] });
              queryClient.invalidateQueries({ queryKey: ["scanning-session-progress"] });
            },
          },
        );
      }
      // ${validationResult}
      speak(`Error. ${validationResult}`);
      setTimeout(() => {
        setValue("vacutainerBarcode", "");
        clearErrors("vacutainerBarcode");
        setFormMessage({ text: "", type: "" });
        setTimeout(() => {
          const input = document.getElementsByName(
            "vacutainerBarcode",
          )[0] as HTMLInputElement;
          if (input) input.focus();
        }, 100);
      }, 2500);
    }
  };

  const generateVacutainerBarcode = (subBarcode: string, session: any): string => {
    if (!subBarcode || subBarcode.length < 6) return subBarcode || "";
    const prefix = subBarcode.substring(0, 6);
    const period = session?.period_sort ?? session?.period_number ?? 1;
    const tpNum = Number(session?.timepoint_sort ?? session?.timepoint_number ?? 1);
    const timepoint = tpNum < 10 ? `0${tpNum}` : `${tpNum}`;
    return `${prefix}${period}${timepoint}1`;
  };

  const handleSkipSubject = () => {
    const subBarcode = subjectBarcode || watch("subjectBarcode");

    if (!subBarcode || subjectStatus !== "matched") {
      setFormMessage({
        text: "Please scan and validate subject barcode before skipping.",
        type: "error",
      });
      speak("Please scan and validate subject barcode before skipping.");
      return;
    }
    setSkipReason("");
    setShowSkipModal(true);
  };

  const handleConfirmSkipSubject = (reasonText: string) => {
    const subBarcode = subjectBarcode || watch("subjectBarcode");
    const vacBarcode = vacutainerBarcode || watch("vacutainerBarcode");

    const targetVacutainer = vacBarcode || generateVacutainerBarcode(subBarcode, sessionData);
    const formattedReason = reasonText.trim();
    const currentUser = GetUserData();

    ScanInsertMutation.mutate(
      {
        sessionid: sessionid ? parseInt(sessionid) : sessionData?.sessionid,
        subjectbarcode: subBarcode,
        vaccutainerbarcode: targetVacutainer,
        result: "skipped",
        resultmessage: "Subject Sample Collection Skipped",
        skipreason: formattedReason,
        scanby: currentUser?.userid || 1,
        isverify: false,
        isinsert: true,
      },
      {
        onSuccess: (res: any) => {
          queryClient.invalidateQueries({
            queryKey: ["scanning-scan-sample-collection-select-all"],
          });
          queryClient.invalidateQueries({ queryKey: ["scanning-session-select"] });
          queryClient.invalidateQueries({ queryKey: ["scanning-session-progress"] });
          queryClient.invalidateQueries({ queryKey: ["sample-collection-time-select-all"] });
          setFormMessage({
            text: res?.message || "Subject Skipped Successfully",
            type: "success",
          });
          setShowSkipModal(false);
          setSkipReason("");
          speak("Subject Skipped.", () => clearAndRestart());
        },
        onError: (err: any) => {
          const errorData = err?.response?.data || err;
          let backendMsg =
            typeof errorData === "string"
              ? errorData
              : errorData?.message ||
                errorData?.resultmessage ||
                err?.message ||
                "Failed to skip subject";
          setFormMessage({ text: backendMsg, type: "error" });
          speak("Failed to skip subject");
        },
      }
    );
  };

  const columns = [
    {
      key: "scandate",
      label: "Date",
      render: (_: any, row?: any) =>
        row ? (
          <div className={`${ac.cellText}`}>
            {formatDateTime(row.scandate).split(" ")[0]}
          </div>
        ) : null,
    },
    {
      key: "scantime",
      label: "Time",
      render: (_: any, row?: any) =>
        row ? (
          <div className={`${ac.cellText}`}>
            {formatDateTime(row.scandate).split(" ").slice(1).join(" ")}
          </div>
        ) : null,
    },
    {
      key: "subjectbarcode",
      label: "Subject",
      render: (_: any, row?: any) =>
        row ? (
          <div className={`fw-600 ${ac.cellText}`}>{row.subjectbarcode}</div>
        ) : null,
    },
    {
      key: "vaccutainerbarcode",
      label: "Vacutainer",
      render: (_: any, row?: any) =>
        row ? (
          <div className={`${ac.cellText}`}>{row.vaccutainerbarcode}</div>
        ) : null,
    },
    {
      key: "result",
      label: "Status",
      render: (_: any, row?: any) =>
        row ? (
          <span
            className={`badge ${row.result === "valid" ? "bg-success" : "bg-danger"} bg-opacity-10 ${row.result === "valid" ? "text-success" : "text-danger"} border ${row.result === "valid" ? "border-success" : "border-danger"} border-opacity-25 px-2 py-1 text-uppercase`}
            style={{ minWidth: "80px", display: "inline-block" }}
          >
            <i
              className={`${row.result === "valid" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} me-1`}
            ></i>{" "}
            {row.result}
          </span>
        ) : null,
    },
    {
      key: "resultmessage",
      label: "Result",
      render: (_: any, row?: any) =>
        row ? (
          <div
            className={`text-muted text-truncate ${ac.cellText}`}
            style={{ maxWidth: "130px" }}
            title={row.resultmessage || "-"}
          >
            {row.resultmessage || "-"}
          </div>
        ) : null,
    },
  ];

  const scanFields = getScanFields("subjectToVacutainer", {
    isSessionCompleted: isCollectionScanCompleted,
    step1Status: subjectStatus,
    step2Status: vacutainerStatus,
    isSpeaking,
    isPending: ScanInsertMutation.isPending,
  });

  const columnsAll = getSessionLogColumns("subjectToVacutainer", ac);

  return (
    <>
      {isShowingAllScans ? (
        (() => {
          const historyData = SelectAllQuery?.data?.data || [];
          const totalRecords = historyData?.[0]?.total_count || 0;
          return (
            <div className={ac.allScansContainer}>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center gap-3">
                  <Button
                    color="light"
                    outline
                    className="px-2 py-1 bg-white border rounded"
                    onClick={() => setIsShowingAllScans(false)}
                  >
                    <i className="ri-arrow-left-line text-dark"></i>
                  </Button>
                  <h5
                    className={`fw-bold text-dark mb-0 d-flex align-items-center gap-2 ${ac.allScansTitle}`}
                  >
                    <i className="ri-history-line text-muted"></i> All Subject
                    to Vacutainer Scans
                    <span
                      className={`badge bg-light text-dark ms-1 border ${ac.allScansBadge}`}
                    >
                      {totalRecords}
                    </span>
                  </h5>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <div
                    className={`${sf.searchBox} ${searchFocused ? sf.focused : ""}`}
                  >
                    <i className={`ri-search-line ${sf.searchIcon}`} />
                    <input
                      type="text"
                      placeholder="Search barcodes..."
                      value={searchText ?? ""}
                      className={sf.searchInput}
                      onChange={(e) => {
                        setSearchText(e.target.value);
                        setcurrentPageNumber(1);
                      }}
                      onFocus={() => setSearchFocused(true)}
                      onBlur={() => setSearchFocused(false)}
                    />
                    {searchText && (
                      <i
                        className={`ri-close-line ${sf.clearIcon}`}
                        onClick={() => {
                          setSearchText("");
                          setcurrentPageNumber(1);
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div>
                <CommonTableIndexWise
                  columns={columnsAll}
                  data={historyData}
                  loading={SelectAllQuery.isFetching}
                  pageSize={10}
                  currentPage={currentPageNumber}
                  totalRecords={totalRecords}
                  onPageChange={setcurrentPageNumber}
                  actions={{}}
                />
              </div>
            </div>
          );
        })()
      ) : (
        <div className={ac.scanContentWrapper}>
          <Row className="gx-1">
            {/* Left Panel */}
            <Col md={5}>
              <Card className="border shadow-none h-100 rounded-0">
                <CardBody className="p-4 pb-0">
                  <div className={ac.sectionHeader}>
                    <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                      <i className="ri-barcode-line text-muted"></i> Barcode
                      Scanning
                    </h6>
                    <p className="text-muted mb-0">
                      Follow the sequence below to accession samples.
                    </p>
                  </div>

                  <Form onSubmit={handleSubmit(handleScanSubmit)}>
                    <button
                      type="submit"
                      name="hiddenSubmit"
                      style={{ display: "none" }}
                    />

                    <CommonForm
                      fields={scanFields}
                      control={control}
                      columns={1}
                    />

                    <div className={ac.formActions}>
                      <Button
                        color="light"
                        outline
                        onClick={clearAndRestart}
                        type="button"
                        disabled={
                          isSpeaking ||
                          ScanInsertMutation.isPending ||
                          isCollectionScanCompleted
                        }
                      >
                        Clear
                      </Button>
                      <Button
                        color="warning"
                        outline
                        type="button"
                        onClick={handleSkipSubject}
                        disabled={
                          subjectStatus !== "matched" ||
                          ScanInsertMutation.isPending ||
                          isSpeaking ||
                          isCollectionScanCompleted
                        }
                      >
                        <i className="ri-skip-forward-line me-1"></i>
                        Skip Subject
                      </Button>
                      <Button
                        color="primary"
                        outline
                        type="submit"
                        name="verifyButton"
                        disabled={
                          !subjectBarcode ||
                          !vacutainerBarcode ||
                          ScanInsertMutation.isPending ||
                          isSpeaking ||
                          isCollectionScanCompleted
                        }
                      >
                        Verify Match
                      </Button>
                    </div>

                    {formMessage.text && (
                      <div
                        className={
                          formMessage.type === "success"
                            ? ac.feedbackSuccess
                            : ac.feedbackError
                        }
                      >
                        <i
                          className={`${formMessage.type === "success" ? "ri-checkbox-circle-fill text-success" : "ri-error-warning-fill text-danger"} fs-18 mt-1`}
                        ></i>
                        <div>
                          <p
                            className={
                              formMessage.type === "success"
                                ? "text-success"
                                : "text-danger"
                            }
                          >
                            <span className="fw-bold">
                              {formMessage.type === "success"
                                ? "Success:"
                                : "Error:"}
                            </span>{" "}
                            {formMessage.text}
                          </p>
                        </div>
                      </div>
                    )}
                  </Form>
                </CardBody>
              </Card>
            </Col>

            {/* Right Panel */}
            <Col md={7}>
              <Card className="border shadow-none mb-0 h-100 rounded-0">
                <CardBody className="p-0 d-flex flex-column h-100">
                  <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                    <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                      <i className="ri-history-line text-muted"></i> Recent
                      Subject to Vacutainer Scans
                    </h6>
                  </div>

                  <div className={`flex-grow-1 p-2 ${ac.scansScrollArea}`}>
                    <CommonTableIndexWise
                      columns={columns}
                      data={[...(SelectAllQuery?.data?.data || [])].reverse().slice(0, 5)}
                      loading={SelectAllQuery.isFetching}
                      maxHeight="200px"
                    />
                  </div>

                  <div
                    className={`p-3 border-top text-center ${ac.viewAllBar}`}
                    onClick={() => setIsShowingAllScans(true)}
                  >
                    <span className="fw-bold text-dark">
                      View All Session Scans
                    </span>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      )}

      {/* ── SKIP REASON MODAL ── */}
      <SkipReasonModal
        isOpen={showSkipModal}
        toggle={() => setShowSkipModal(false)}
        subjectBarcode={subjectBarcode || watch("subjectBarcode") || ""}
        onSubmit={handleConfirmSkipSubject}
        isPending={ScanInsertMutation.isPending}
      />
    </>
  );
};

export default SubjectToVacutainerScanning;
